import formatDate from 'date-fns/format';
import type { VoidFunctionComponent } from 'react';

import { BannerAttention, BannerSize, BannerStyle } from '../../../../typings/enums';
import Banner from '../../generic/Banner';
import NavigationButton from '../../generic/Button/NavigationButton';

/**
 * The card only applies to revisions that have been analyzed and only when the revision
 * actually has dependencies to preserve. `dependencyCount` is set at parse time before either
 * store is chosen, so a `0` means there was never anything written to DependencyLocks or
 * Sparkle -- the card doesn't apply, and this stays true after the DependencyLocks table is
 * dropped. Dependencies are read from Sparkle only, so callers gate this on the revision having
 * no Sparkle analysis; every analyzed, non-empty revision that passes that gate has lost its
 * dependencies. Written as a type predicate so call sites can pass the narrowed date straight
 * through to the card.
 */
export const shouldShowAnalyzedWithoutSparkleCard = (
  revisionAnalyzedAt: string | Date | null | undefined,
  dependencyCount: number | null | undefined,
): revisionAnalyzedAt is string | Date =>
  !!revisionAnalyzedAt && dependencyCount !== 0;

type Params = {
  /** When the revision was last analyzed (`Revision.dependency_cache_valid`). */
  revisionAnalyzedAt: string | Date;
};

export const AnalyzedWithoutSparkleCard: VoidFunctionComponent<Params> = ({ revisionAnalyzedAt }) => {
  const analyzedDate = new Date(revisionAnalyzedAt);
  const bodyMessage = `This revision was last analyzed on ${formatDate(analyzedDate, 'MM/dd/yyyy')} and its dependencies are not yet included in FOSSA's new dependency database.`;
  const action = (
    <NavigationButton
      type="error"
      size="medium"
      to="https://docs.fossa.com/docs/help/dependencylocks-deprecation"
      openInNewTab
    >
      Learn More
    </NavigationButton>
  );
  return (
    <Banner
      bannerSize={BannerSize.Medium}
      bannerStyle={BannerStyle.High}
      bannerAttention={BannerAttention.Low}
      title="Action required: re-analyze this revision to preserve historical dependency data"
      body={bodyMessage}
      actions={action}
    />
  );
};
